import { EmployerOption } from 'hooks/webapi/foxentry/useCompaniesByIdMutation';
import { Education, EmploymentStatus } from 'hooks/webapi/useClient';
import { ClientHousehold } from 'hooks/webapi/useClientHousehold';
import i18Next from 'i18next';
import {
  applyAll,
  employerValidation,
  empty,
  firstNumLarger,
  rangeValue,
} from 'utils/validations';

export type BudgetForm = {
  monthlyIncome?: number;
  monthlyExpenses?: number;
  numberOfHouseholdMembers?: number;
  numberOfEmployedHouseholdMembers?: number;
  housingType?: ClientHousehold['ownership'];
  educationType?: Education;
  incomeSourceType?: EmploymentStatus;
  employer?: EmployerOption | string | undefined;
};

type FormErrors = Partial<Record<keyof BudgetForm, string>>;

export default function validateFinanceForm(values: BudgetForm) {
  const errors: FormErrors = {
    monthlyIncome: applyAll(
      empty,
      firstNumLarger(
        Number(values.monthlyExpenses),
        Number(values.monthlyIncome),
        i18Next.t('error:validation.income_smaller_than_expenses'),
      ),
    )(values.monthlyIncome),
    monthlyExpenses: applyAll(
      empty,
      firstNumLarger(
        100,
        Number(values.monthlyExpenses),
        i18Next.t('error:validation.expenses_too_low'),
      ),
    )(values.monthlyExpenses),
    numberOfHouseholdMembers: applyAll(
      empty,
      rangeValue(
        1,
        20,
        i18Next.t('error:validation.household_members_maxamount'),
      ),
      firstNumLarger(
        Number(values.numberOfEmployedHouseholdMembers),
        Number(values.numberOfHouseholdMembers),
        i18Next.t('error:validation.household_members_toosmall'),
      ),
    )(values.numberOfHouseholdMembers),
    numberOfEmployedHouseholdMembers: applyAll(
      empty,
      rangeValue(
        1,
        20,
        i18Next.t('error:validation.household_members_maxamount'),
      ),
      firstNumLarger(
        Number(values.numberOfEmployedHouseholdMembers),
        Number(values.numberOfHouseholdMembers),
        i18Next.t('error:validation.household_employedmembers_toobig'),
      ),
    )(values.numberOfEmployedHouseholdMembers),
    housingType: applyAll(empty)(values.housingType),
    educationType: applyAll(empty)(values.educationType),
    incomeSourceType: applyAll(empty)(values.incomeSourceType),
    employer: applyAll(employerValidation(values.incomeSourceType))(
      values.employer,
    ),
  };

  const hasAnyError = Object.values(errors).filter(Boolean).length > 0;
  return hasAnyError ? errors : {};
}
